import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useTenants } from "@/hooks/useTenants";
import { CompanyInfoStep } from "./wizard/CompanyInfoStep";
import { UserInfoStep } from "./wizard/UserInfoStep";
import { PlanSelectionStep } from "./wizard/PlanSelectionStep";
import { PlanCustomizationStep } from "./wizard/PlanCustomizationStep";
import { ReviewStep } from "./wizard/ReviewStep";
import { SuccessModal } from "./wizard/SuccessModal";
import { EmailExistsDialog } from "./wizard/EmailExistsDialog";

const formSchema = z.object({
  // Company
  companyName: z.string().min(3, "Mínimo 3 caracteres").max(100, "Máximo 100 caracteres"),
  companyCnpj: z.string().optional(),
  companyEmail: z.string().email("E-mail inválido").optional().or(z.literal("")),
  companyPhone: z.string().optional(),
  companyDomain: z.string().optional(),
  
  // User
  userName: z.string().min(3, "Mínimo 3 caracteres"),
  userEmail: z.string().email("E-mail inválido"),
  userPassword: z.string().min(6, "Mínimo 6 caracteres"),
  confirmPassword: z.string(),
  userPhone: z.string().optional(),
  
  // Plan
  planId: z.string().min(1, "Selecione um plano"),
  customAdvancedMetrics: z.array(z.string()).optional(),
  customIntegrations: z.array(z.string()).optional(),
}).refine((data) => data.userPassword === data.confirmPassword, {
  message: "As senhas não coincidem",
  path: ["confirmPassword"],
});

type FormValues = z.infer<typeof formSchema>;

interface CreateDemoTenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateDemoTenantDialog({ open, onOpenChange }: CreateDemoTenantDialogProps) {
  const [step, setStep] = useState(1);
  const [successData, setSuccessData] = useState<any>(null);
  const [emailErrorOpen, setEmailErrorOpen] = useState(false);
  const { createDemoTenant } = useTenants();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companyName: "",
      companyCnpj: "",
      companyEmail: "",
      companyPhone: "",
      companyDomain: "",
      userName: "",
      userEmail: "",
      userPassword: "",
      confirmPassword: "",
      userPhone: "",
      planId: "",
      customAdvancedMetrics: [],
      customIntegrations: [],
    },
  });

  const handleNext = async () => {
    let fieldsToValidate: (keyof FormValues)[] = [];
    
    if (step === 1) {
      fieldsToValidate = ["companyName"];
    } else if (step === 2) {
      fieldsToValidate = ["userName", "userEmail", "userPassword", "confirmPassword"];
    } else if (step === 3) {
      fieldsToValidate = ["planId"];
    } else if (step === 4) {
      // Customizações são opcionais - sem validação
      fieldsToValidate = [];
    }

    const isValid = await form.trigger(fieldsToValidate);
    if (isValid) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const onSubmit = async (values: FormValues) => {
    createDemoTenant.mutate(values, {
      onSuccess: (data) => {
        setSuccessData(data);
      },
      onError: (error: any) => {
        // Verificar se é erro de e-mail duplicado
        const errorMessage = error?.userFriendlyMessage || 
                            error?.error?.userFriendlyMessage || 
                            error?.message || '';
        
        if (errorMessage.toLowerCase().includes('e-mail já cadastrado') || 
            errorMessage.toLowerCase().includes('email já cadastrado')) {
          setEmailErrorOpen(true);
          setStep(2); // Volta para o step do usuário
        }
      },
    });
  };

  const handleClose = () => {
    form.reset();
    setStep(1);
    setSuccessData(null);
    setEmailErrorOpen(false);
    onOpenChange(false);
  };

  const handleEmailErrorConfirm = () => {
    setEmailErrorOpen(false);
    // Limpa apenas o campo de e-mail para facilitar a correção
    form.setValue('userEmail', '');
    form.clearErrors('userEmail');
  };

  if (successData) {
    return (
      <SuccessModal
        open={open}
        data={successData}
        onClose={handleClose}
        onCreateAnother={() => {
          setSuccessData(null);
          form.reset();
          setStep(1);
        }}
      />
    );
  }

  return (
    <>
      <EmailExistsDialog 
        open={emailErrorOpen}
        onConfirm={handleEmailErrorConfirm}
        email={form.getValues('userEmail')}
      />
      
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Criar Tenant de Demonstração</DialogTitle>
          <DialogDescription>
            Preencha os dados para criar uma nova tenant demo
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center gap-1 py-4 px-6 overflow-x-hidden">
          <div className={`flex items-center ${step >= 1 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`rounded-full w-8 h-8 flex items-center justify-center border-2 ${step >= 1 ? 'border-primary bg-primary text-primary-foreground' : 'border-muted'}`}>
              1
            </div>
            <span className="ml-1 text-xs font-medium whitespace-nowrap">Empresa</span>
          </div>
          
          <div className={`h-0.5 w-6 ${step >= 2 ? 'bg-primary' : 'bg-muted'}`} />
          
          <div className={`flex items-center ${step >= 2 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`rounded-full w-8 h-8 flex items-center justify-center border-2 ${step >= 2 ? 'border-primary bg-primary text-primary-foreground' : 'border-muted'}`}>
              2
            </div>
            <span className="ml-1 text-xs font-medium whitespace-nowrap">Usuário</span>
          </div>
          
          <div className={`h-0.5 w-6 ${step >= 3 ? 'bg-primary' : 'bg-muted'}`} />
          
          <div className={`flex items-center ${step >= 3 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`rounded-full w-8 h-8 flex items-center justify-center border-2 ${step >= 3 ? 'border-primary bg-primary text-primary-foreground' : 'border-muted'}`}>
              3
            </div>
            <span className="ml-1 text-xs font-medium whitespace-nowrap">Plano</span>
          </div>
          
          <div className={`h-0.5 w-6 ${step >= 4 ? 'bg-primary' : 'bg-muted'}`} />
          
          <div className={`flex items-center ${step >= 4 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`rounded-full w-8 h-8 flex items-center justify-center border-2 ${step >= 4 ? 'border-primary bg-primary text-primary-foreground' : 'border-muted'}`}>
              4
            </div>
            <span className="ml-1 text-xs font-medium whitespace-nowrap">Config.</span>
          </div>
          
          <div className={`h-0.5 w-6 ${step >= 5 ? 'bg-primary' : 'bg-muted'}`} />
          
          <div className={`flex items-center ${step >= 5 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`rounded-full w-8 h-8 flex items-center justify-center border-2 ${step >= 5 ? 'border-primary bg-primary text-primary-foreground' : 'border-muted'}`}>
              5
            </div>
            <span className="ml-1 text-xs font-medium whitespace-nowrap">Revisar</span>
          </div>
        </div>

        <Form {...form}>
          <form 
            onSubmit={(e) => {
              e.preventDefault();
              if (step !== 5) {
                return;
              }
              form.handleSubmit(onSubmit)(e);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
            className="space-y-4"
          >
            {step === 1 && <CompanyInfoStep form={form} />}
            {step === 2 && <UserInfoStep form={form} />}
            {step === 3 && <PlanSelectionStep form={form} />}
            {step === 4 && <PlanCustomizationStep form={form} />}
            {step === 5 && <ReviewStep form={form} />}

            <DialogFooter className="gap-2">
              {step > 1 && (
                <Button type="button" variant="outline" onClick={handleBack}>
                  Voltar
                </Button>
              )}
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
              {step < 5 ? (
                <Button type="button" onClick={handleNext}>
                  Próximo
                </Button>
              ) : (
                <Button 
                  type="button" 
                  onClick={() => form.handleSubmit(onSubmit)()} 
                  disabled={createDemoTenant.isPending}
                >
                  {createDemoTenant.isPending ? "Criando..." : "Criar Tenant"}
                </Button>
              )}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
    </>
  );
}
